import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, InputAdornment, Chip, tableCellClasses, CircularProgress,
  Fab, Fade, Tooltip, Avatar, Collapse, IconButton, Select, MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAppDispatch } from '../../store/hooks';
import { fetchAuditLogs, fetchAuditFilterOptions } from '../../store/auditLogStore/auditLogThunk';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";
import moment from 'moment';
import { IAuditLog, IAuditLogFilters } from '../../utils/auditLog.types';
import { ApiStatus } from '../../utils/api.types';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'top',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:last-child td, &:last-child th': {},
}));

const ACTION_COLORS: Record<string, string> = {
  CREATE: '#059669',
  UPDATE: '#3b82f6',
  DELETE: '#dc2626',
  BOOKED: '#7c3aed',
  APPROVED: '#059669',
  REJECTED: '#dc2626',
  DISPATCHED: '#0891b2',
  CANCELLED: '#f59e0b',
  SETTLED: '#059669',
  PAID: '#059669',
  SENT: '#3b82f6',
  VOIDED: '#dc2626',
  RESTORED: '#f59e0b',
  CONVERTED: '#7c3aed',
  DUPLICATED: '#6b7280',
  LOGIN: '#0d9488',
  LOGIN_FAILED: '#dc2626',
  LOGOUT: '#64748b',
  FAILED: '#dc2626',
};

const ACTION_OPTIONS = [
  '', 'LOGIN', 'LOGIN_FAILED', 'LOGOUT', 'CREATE', 'UPDATE', 'DELETE', 'BOOKED', 'APPROVED', 'REJECTED',
  'DISPATCHED', 'CANCELLED', 'SETTLED', 'PAID', 'SENT', 'FAILED', 'VOIDED', 'RESTORED',
];

const formatDuration =(sec?: number) => {
  if (sec === undefined || sec === null) return '';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m`;
  return `${sec}s`;
};

// Sign-in / sign-out entries: their description gets a device-spec line underneath.
const AUTH_ACTIONS = ['LOGIN', 'LOGIN_FAILED', 'LOGOUT'];

// Where the action was done (metadata.source); the backend treats 'Mobile' as any app entry.
const SOURCE_OPTIONS = [
  { value: 'Web', label: 'Web' },
  { value: 'Mobile', label: 'Mobile App' },
  { value: 'System', label: 'System' },
];

/** Compact line under an outbound-notification entry: "WhatsApp · to ****0836 · System". */
const describeNotification = (entry: IAuditLog) => {
  const n = entry.metadata?.notification;
  if (!n) return null;
  const specs = [
    n.channel,
    n.recipient && `to ${n.recipient}`,
    n.trigger === 'System' ? 'Automatic' : 'Triggered by user',
  ].filter(Boolean).join(' · ');

  const details: [string, any][] = [
    ['Channel', n.channel],
    ['Notification', n.kind],
    ['Template', n.template],
    ['Recipient', n.recipient],
    ['Triggered by', n.trigger === 'System' ? 'System (automatic)' : 'A user action'],
    ['Failure reason', n.error],
  ];

  return { specs, details: details.filter(([, value]) => value !== undefined && value !== null && value !== '') };
};

/** "Android App 0.0.28+28" for entries made from the Wareozo mobile app; null for web. */
const appSourceLabel = (entry: IAuditLog) => {
  const source = entry.metadata?.source;
  if (!source || !/App$/.test(source)) return null;
  const version = entry.metadata?.appVersion;
  return version ? `${source} ${version}` : source;
};

/** Compact spec line ("Desktop · IP 49.36.1.2 · 1920x1080 · …") plus the full detail list for the tooltip. */
const describeDevice = (entry: IAuditLog) => {
  const d = entry.metadata?.device || {};
  const rawIp = (entry.metadata?.ipAddress || '').replace(/^::ffff:/, '');
  // ::1 / 127.0.0.1 = the request came from the same machine as the API (local dev).
  const ip = rawIp === '::1' || rawIp === '127.0.0.1' ? `localhost (${rawIp})` : rawIp;
  const isApp = d.browser === 'Wareozo App';
  const browserVersion = isApp ? d.browserVersion : d.browserVersion?.split('.')[0];
  const browser = d.browser ? `${d.browser}${browserVersion ? ` ${browserVersion}` : ''}` : '';
  const os = d.os ? `${d.os}${d.osVersion ? ` ${d.osVersion}` : ''}` : '';
  if (!browser && !os && !ip) return null;

  const details: [string, any][] = [
    ['IP address', ip],
    [isApp ? 'App' : 'Browser', d.browser && `${d.browser} ${d.browserVersion || ''}${isApp && d.appBuild ? ` (build ${d.appBuild})` : ''}`.trim()],
    ['OS', os],
    ['Device', d.deviceType],
    ['Model', d.deviceModel],
    ['Manufacturer', d.manufacturer],
    ['Emulator', d.isPhysicalDevice === false ? 'Yes' : undefined],
    ['Platform', d.platform],
    ['Sign-in method', entry.metadata?.loginMethod],
    ['Timezone', d.timezone],
    ['CPU', d.cores && `${d.cores} cores`],
    ['RAM', d.memory && `${d.memory} GB`],
    ['Network', d.connection],
  ];

  // Browser / OS are already in the description text ("… from Chrome 152 on Windows 11"), so not repeated here.
  // Hardware/locale specs live in the tooltip only; the visible line stays IP · device · session.
  const session = formatDuration(entry.metadata?.sessionDurationSec);
  const specs = [
    ip && `IP ${ip}`,
    d.deviceModel || d.deviceType,
    session && `Session ${session}`,
  ].filter(Boolean).join(' · ');

  return {
    specs,
    details: details.filter(([, value]) => value !== undefined && value !== null && value !== ''),
  };
};

const AuditLog: React.FC = () => {
  const dispatch = useAppDispatch();

  const [allEntries, setAllEntries] = useState<IAuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [rowsPerPage] = useState(30);
  const [total, setTotal] = useState(0);
  const requestGen = useRef(0);
  const [searchTerm, setSearchTerm] = useState('');
  // Search runs on the server over all logs (not just the loaded page); debounced while typing
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  // Shared Filters date dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD"; starts at its Last 30 Days default
  const [dateRange, setDateRange] = useState({ start: moment().subtract(30, 'days').format('YYYY-MM-DD'), end: moment().format('YYYY-MM-DD') });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  // Module -> entities actually logged; Record Type options follow the chosen module
  const [filterOptions, setFilterOptions] = useState<{ module: string; entities: string[] }[]>([]);
  const [userOptions, setUserOptions] = useState<{ _id: string; name: string; email: string }[]>([]);
  const moduleOptions = filterOptions.map(o => o.module).filter(Boolean);
  const entityOptions = Array.from(new Set(filterOptions
    .filter(o => !moduleFilter || o.module === moduleFilter)
    .flatMap(o => o.entities))).sort();

  useEffect(() => {
    dispatch(fetchAuditFilterOptions()).unwrap()
      .then(({ modules, users }) => { setFilterOptions(modules); setUserOptions(users); })
      .catch(() => { setFilterOptions([]); setUserOptions([]); });
  }, [dispatch]);

  const changeModule = (value: string) => {
    setModuleFilter(value);
    // Drop a record type that doesn't belong to the newly chosen module
    if (value && entityFilter && !filterOptions.find(o => o.module === value)?.entities.includes(entityFilter)) setEntityFilter('');
  };

  // Scroll
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);

  const fetchReport = useCallback((pageNum: number = 1, append: boolean = false) => {
    // A filter change starts a new generation; responses from an older one are dropped
    if (!append) requestGen.current += 1;
    const gen = requestGen.current;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    const filters: IAuditLogFilters = {
      page: pageNum,
      limit: rowsPerPage,
    };
    if (entityFilter) filters.entity = entityFilter;
    if (actionFilter) filters.action = actionFilter;
    if (moduleFilter) filters.module = moduleFilter;
    if (sourceFilter) filters.source = sourceFilter;
    if (userFilter) filters.userId = userFilter;
    if (debouncedSearch) filters.search = debouncedSearch;
    if (dateRange.start) filters.from = moment(dateRange.start).startOf('day').toISOString();
    if (dateRange.end) filters.to = moment(dateRange.end).endOf('day').toISOString();

    dispatch(fetchAuditLogs(filters))
      .unwrap()
      .then((response) => {
        if (gen !== requestGen.current) return;
        if (response.success) {
          if (append) {
            setAllEntries(prev => [...prev, ...response.logs]);
          } else {
            setAllEntries(response.logs);
          }
          setHasMore(response.pagination.page < response.pagination.pages);
          setTotal(response.pagination.total);
        }
      })
      .catch((error) => {
        if (gen !== requestGen.current) return;
        toast.error(error.message || 'Failed to fetch audit logs');
      })
      .finally(() => {
        if (gen !== requestGen.current) return;
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  }, [dispatch, rowsPerPage, entityFilter, actionFilter, moduleFilter, sourceFilter, userFilter, dateRange, debouncedSearch]);

  // Initial load
  useEffect(() => {
    setPage(1);
    setAllEntries([]);
    fetchReport(1, false);
  }, [entityFilter, actionFilter, moduleFilter, sourceFilter, userFilter, dateRange, debouncedSearch]);

  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    const start = params.get('start') || '';
    const end = params.get('end') || '';
    setDateRange(prev => (prev.start === start && prev.end === end) ? prev : { start, end });
  };

  // Debounced search - client-side filter on loaded data
  const filteredEntries = allEntries;

  // Load more
  const loadMore = () => {
    if (isLoadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReport(nextPage, true);
  };

  // Scroll handler
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const currentScrollTop = scrollContainerRef.current.scrollTop;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    if (currentScrollTop > lastScrollTop.current) {
      setScrollDirection('down');
    } else if (currentScrollTop < lastScrollTop.current) {
      setScrollDirection('up');
    }
    lastScrollTop.current = currentScrollTop;

    if (currentScrollTop > 100) {
      setShowScrollButton(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => setShowScrollButton(false), 2000);
    }

    // Infinite scroll
    const distanceFromBottom = scrollHeight - clientHeight - currentScrollTop;
    if (distanceFromBottom < 200 && hasMore && !isLoadingMore && !isLoading) {
      loadMore();
    }
  };

  const handleScrollButtonClick = () => {
    if (!scrollContainerRef.current) return;
    if (scrollDirection === 'down') {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
    } else {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2, position: 'relative' }} className={'c-card2'}>
          {/* Scrollable content area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>

            {/* Report Header */}
            <ReusableReportHeader
              reportType="AUDIT_LOG"
              title="Activity Logs"
              subtitle="Activity log across all modules"
              recordCount={total}
              isLoading={isLoading}
              showPrint={false}
              showShare={false}
            />

            {/* Filters Row */}
            <Grid container px={2} py={1.5} alignItems="center">
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {/* Refresh */}
                  <IconButton
                    size="small"
                    onClick={() => { setPage(1); setAllEntries([]); fetchReport(1, false); }}
                    style={{ marginRight: '10px', marginLeft: '5px', color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1' }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>

                  {/* Date Range (shared Filters component) */}
                  <Filters type="audit-log" hidePartySearch hideFilter hideReset inline filterHandler={handleDateFilter} />

                  {/* User Filter */}
                  <Select
                    name="user"
                    disableUnderline
                    displayEmpty
                    className='c-text-field-3'
                    variant="outlined"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value as string)}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 300, '& .MuiMenuItem-root': { fontSize: 13 } },
                      },
                    }}
                    sx={{
                      minWidth: 140, maxWidth: 220, fontSize: 14,
                      boxShadow: 'none',
                      pt: '0px !important',
                      pl: '10px !important',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      '.MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13 }}>All Users</MenuItem>
                    {userOptions.map(u => (
                      <MenuItem key={u._id} value={u._id} sx={{ fontSize: 13 }}>{u.name}</MenuItem>
                    ))}
                  </Select>

                  {/* Module Filter */}
                  <Select
                    name="module"
                    disableUnderline
                    displayEmpty
                    className='c-text-field-3'
                    variant="outlined"
                    value={moduleFilter}
                    onChange={(e) => changeModule(e.target.value as string)}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 300, '& .MuiMenuItem-root': { fontSize: 13 } },
                      },
                    }}
                    sx={{
                      minWidth: 140, fontSize: 14,
                      boxShadow: 'none',
                      pt: '0px !important',
                      pl: '10px !important',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      '.MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13 }}>All Modules</MenuItem>
                    {moduleOptions.map(m => (
                      <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m.charAt(0).toUpperCase() + m.slice(1)}</MenuItem>
                    ))}
                  </Select>

                  {/* Record Type Filter (options follow the chosen module) */}
                  <Select
                    name="entity"
                    disableUnderline
                    displayEmpty
                    className='c-text-field-3'
                    variant="outlined"
                    value={entityFilter}
                    onChange={(e) => setEntityFilter(e.target.value as string)}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 300, '& .MuiMenuItem-root': { fontSize: 13 } },
                      },
                    }}
                    sx={{
                      minWidth: 150, fontSize: 14,
                      boxShadow: 'none',
                      pt: '0px !important',
                      pl: '10px !important',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      '.MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13 }}>All Record Types</MenuItem>
                    {entityOptions.map(e => (
                      <MenuItem key={e} value={e} sx={{ fontSize: 13 }}>{e}</MenuItem>
                    ))}
                  </Select>

                  {/* Action Filter */}
                  <Select
                    name="action"
                    disableUnderline
                    displayEmpty
                    className='c-text-field-3'
                    variant="outlined"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value as string)}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 300, '& .MuiMenuItem-root': { fontSize: 13 } },
                      },
                    }}
                    sx={{
                      minWidth: 140, fontSize: 14,
                      boxShadow: 'none',
                      pt: '0px !important',
                      pl: '10px !important',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      '.MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13 }}>All Actions</MenuItem>
                    {ACTION_OPTIONS.filter(Boolean).map(a => (
                      <MenuItem key={a} value={a} sx={{ fontSize: 13 }}>{a}</MenuItem>
                    ))}
                  </Select>

                  {/* Source Filter (web / mobile app) */}
                  <Select
                    name="source"
                    disableUnderline
                    displayEmpty
                    className='c-text-field-3'
                    variant="outlined"
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value as string)}
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 300, '& .MuiMenuItem-root': { fontSize: 13 } },
                      },
                    }}
                    sx={{
                      minWidth: 140, fontSize: 14,
                      boxShadow: 'none',
                      pt: '0px !important',
                      pl: '10px !important',
                      '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                      '& .MuiSelect-select': { p: 0 },
                      '.MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13 }}>All Sources</MenuItem>
                    {SOURCE_OPTIONS.map(s => (
                      <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13 }}>{s.label}</MenuItem>
                    ))}
                  </Select>

                  {/* Search */}
                  <TextField
                    className='c-text-field-3'
                    variant="standard"
                    size="small"
                    placeholder="Search user, device, IP, description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: '280px' }}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Active Filters Display */}
            {(entityFilter || actionFilter || moduleFilter || sourceFilter || userFilter) && (
              <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#666' }}>
                  Filters:
                </Typography>
                {entityFilter && (
                  <Chip label={`Record Type: ${entityFilter}`} size="small" onDelete={() => setEntityFilter('')}
                    sx={{ fontSize: '0.75rem', height: '24px', backgroundColor: '#e3f2fd' }} />
                )}
                {actionFilter && (
                  <Chip label={`Action: ${actionFilter}`} size="small" onDelete={() => setActionFilter('')}
                    sx={{ fontSize: '0.75rem', height: '24px', backgroundColor: '#e8f5e9' }} />
                )}
                {moduleFilter && (
                  <Chip label={`Module: ${moduleFilter}`} size="small" onDelete={() => setModuleFilter('')}
                    sx={{ fontSize: '0.75rem', height: '24px', backgroundColor: '#fff3e0' }} />
                )}
                {sourceFilter && (
                  <Chip label={`Source: ${SOURCE_OPTIONS.find(s => s.value === sourceFilter)?.label || sourceFilter}`} size="small"
                    onDelete={() => setSourceFilter('')}
                    sx={{ fontSize: '0.75rem', height: '24px', backgroundColor: '#ede9fe' }} />
                )}
                {userFilter && (
                  <Chip label={`User: ${userOptions.find(u => u._id === userFilter)?.name || userFilter}`} size="small"
                    onDelete={() => setUserFilter('')}
                    sx={{ fontSize: '0.75rem', height: '24px', backgroundColor: '#fce7f3' }} />
                )}
              </Box>
            )}

            {/* Summary */}
            {!isLoading && allEntries.length > 0 && (
              <Box sx={{ px: 2, py: 1, backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      <strong>Total Entries:</strong> {total}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                      <strong>Showing:</strong> {filteredEntries.length} entries
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, textAlign: 'right' }}>
                      <strong>Loaded:</strong> {allEntries.length} of {total}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {!isLoading ? (
              <>
                <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small' aria-label="audit log table">
                  <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '35%' }} />
                    <col style={{ width: '4%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Date & Time</StyledTableCell>
                      <StyledTableCell>User</StyledTableCell>
                      <StyledTableCell>Action</StyledTableCell>
                      <StyledTableCell>Record Type</StyledTableCell>
                      <StyledTableCell>Reference</StyledTableCell>
                      <StyledTableCell>Module</StyledTableCell>
                      <StyledTableCell>Description</StyledTableCell>
                      <StyledTableCell></StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEntries.length > 0 ? filteredEntries.map((entry, index) => (
                      <React.Fragment key={`${entry._id}-${index}`}>
                        <StyledTableRow>
                          {/* Date & Time */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.4' }}>
                              <Typography variant="body2" fontWeight={500} fontSize="9pt">
                                {moment(entry.createdAt).format('DD-MM-YYYY')}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '7pt' }}>
                                {moment(entry.createdAt).format('hh:mm A')}
                              </Typography>
                            </div>
                          </StyledTableCell>

                          {/* User */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: '#059669' }}>
                                {(entry.user?.name || entry.userName || 'S').charAt(0).toUpperCase()}
                              </Avatar>
                              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3', overflow: 'hidden' }}>
                                <Tooltip title={`${entry.user?.name || entry.userName || 'System'}${entry.userRole ? ` (${entry.userRole.charAt(0).toUpperCase() + entry.userRole.slice(1)})` : ''}`} arrow placement="top">
                                  <Typography variant="body2" fontWeight={500} fontSize="9pt" noWrap sx={{ maxWidth: 160 }}>
                                    {entry.user?.name || entry.userName || 'System'}
                                    {entry.userRole && (
                                      <span style={{ fontWeight: 400, color: '#6b7280' }}>{` (${entry.userRole.charAt(0).toUpperCase() + entry.userRole.slice(1)})`}</span>
                                    )}
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '7pt' }} noWrap>
                                  {entry.user?.email || entry.userEmail || ''}
                                </Typography>
                              </div>
                            </Box>
                          </StyledTableCell>

                          {/* Action */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <Chip
                              label={entry.action}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '8pt',
                                height: '22px',
                                fontWeight: 600,
                                borderColor: ACTION_COLORS[entry.action] || '#6b7280',
                                color: ACTION_COLORS[entry.action] || '#6b7280',
                              }}
                            />
                          </StyledTableCell>

                          {/* Entity */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" fontSize="9pt" fontWeight={500}>
                              {entry.entity}
                            </Typography>
                          </StyledTableCell>

                          {/* Reference */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <Typography variant="body2" fontSize="9pt" fontWeight={500} sx={{ color: '#1e40af' }}>
                              {entry.entityNumber ? `#${entry.entityNumber}` : '-'}
                            </Typography>
                          </StyledTableCell>

                          {/* Module */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            {entry.module && (
                              <Chip
                                label={entry.module.charAt(0).toUpperCase() + entry.module.slice(1)}
                                size="small"
                                sx={{ fontSize: '7pt', height: '18px', bgcolor: '#f0f0f0' }}
                              />
                            )}
                          </StyledTableCell>

                          {/* Description — sign-in / sign-out entries also carry the device specs */}
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <Typography component="div" variant="body2" fontSize="9pt" sx={{ color: '#292d35' }}>
                              {entry.description || '-'}
                              {entry.metadata?.isNewDevice && (
                                <Chip
                                  label="NEW DEVICE"
                                  size="small"
                                  sx={{ ml: 0.75, fontSize: '6.5pt', height: '16px', fontWeight: 700, bgcolor: '#fff7ed', color: '#c2410c' }}
                                />
                              )}
                              {appSourceLabel(entry) && (
                                <Chip
                                  label={appSourceLabel(entry)}
                                  size="small"
                                  sx={{ ml: 0.75, fontSize: '6.5pt', height: '16px', fontWeight: 700, bgcolor: '#ede9fe', color: '#6d28d9' }}
                                />
                              )}
                            </Typography>
                            {entry.entity === 'Notification' && (() => {
                              const note = describeNotification(entry);
                              if (!note || !note.specs) return null;
                              return (
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    <Box sx={{ fontSize: 11, lineHeight: 1.6 }}>
                                      {note.details.map(([label, value]) => (
                                        <div key={label}><strong>{label}:</strong> {String(value)}</div>
                                      ))}
                                    </Box>
                                  }
                                >
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: entry.action === 'FAILED' ? '#b91c1c' : '#6b7280', fontSize: '7.5pt', cursor: 'default' }}>
                                    {note.specs}
                                  </Typography>
                                </Tooltip>
                              );
                            })()}
                            {AUTH_ACTIONS.includes(entry.action) && (() => {
                              const device = describeDevice(entry);
                              if (!device || !device.specs) return null;
                              return (
                                <Tooltip
                                  arrow
                                  placement="top"
                                  title={
                                    <Box sx={{ fontSize: 11, lineHeight: 1.6 }}>
                                      {device.details.map(([label, value]) => (
                                        <div key={label}><strong>{label}:</strong> {String(value)}</div>
                                      ))}
                                    </Box>
                                  }
                                >
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#6b7280', fontSize: '7.5pt', cursor: 'default' }}>
                                    {device.specs}
                                  </Typography>
                                </Tooltip>
                              );
                            })()}
                          </StyledTableCell>

                          {/* Expand */}
                          <StyledTableCell style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                            {((entry.changes && entry.changes.length > 0) || entry.snapshot) && (
                              <IconButton size="small" onClick={() => toggleExpand(entry._id)} sx={{ p: 0 }}>
                                {expandedRow === entry._id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>

                        {/* Expanded Changes Row */}
                        {expandedRow === entry._id && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ p: 0, borderBottom: '1px solid #d9d4d4' }}>
                              <Collapse in={expandedRow === entry._id}>
                                <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                  {entry.changes && entry.changes.length > 0 ? (
                                    <>
                                      <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block', color: '#555' }}>
                                        Changes ({entry.changes.length} fields modified)
                                      </Typography>
                                      <Table size="small" sx={{ maxWidth: 600 }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '8pt', py: 0.5, color: '#555' }}>Field</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '8pt', py: 0.5, color: '#555' }}>Old Value</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '8pt', py: 0.5, color: '#555' }}>New Value</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {entry.changes.map((change, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell sx={{ fontSize: '8pt', py: 0.5, fontWeight: 500 }}>{change.field}</TableCell>
                                              <TableCell sx={{ fontSize: '8pt', py: 0.5, color: '#d32f2f', textDecoration: 'line-through' }}>
                                                {change.oldValue !== null && change.oldValue !== undefined ? String(change.oldValue) : '-'}
                                              </TableCell>
                                              <TableCell sx={{ fontSize: '8pt', py: 0.5, color: '#2e7d32' }}>
                                                {change.newValue !== null && change.newValue !== undefined ? String(change.newValue) : '-'}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </>
                                  ) : entry.snapshot ? (
                                    <>
                                      <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block', color: '#555' }}>
                                        {entry.action === 'CREATE' ? 'Created with data:' : 'Deleted record data:'}
                                      </Typography>
                                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                        <pre style={{ fontSize: 10, margin: 0, whiteSpace: 'pre-wrap', color: '#333' }}>
                                          {JSON.stringify(entry.snapshot, null, 2)}
                                        </pre>
                                      </Box>
                                    </>
                                  ) : null}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )) : (
                      <StyledTableRow>
                        <StyledTableCell colSpan={8} style={{ textAlign: 'center', padding: 20 }}>
                          No audit records found
                        </StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Loading More Indicator */}
                {isLoadingMore && (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={30} color="info" />
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                      Loading more entries...
                    </Typography>
                  </Box>
                )}

                {/* Showing entries info */}
                {!isLoading && allEntries.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {filteredEntries.length} of {total} entries
                      {!hasMore && allEntries.length > 0 && ' - All entries loaded'}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Grid container p={2}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                  <CircularProgress color="info" />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Scroll to Top/Bottom Button */}
          <Fade in={showScrollButton}>
            <Fab
              color="primary"
              size="medium"
              onClick={handleScrollButtonClick}
              sx={{
                position: 'absolute',
                bottom: 16,
                right: 16,
                zIndex: 1000,
                boxShadow: 3,
                '&:hover': { boxShadow: 6, transform: 'scale(1.05)' },
                transition: 'all 0.3s ease-in-out'
              }}
              aria-label={scrollDirection === 'down' ? 'scroll to bottom' : 'scroll to top'}
            >
              {scrollDirection === 'down' ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </Fab>
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

export default AuditLog;
